import LogRocket from 'logrocket';
import { Mixpanel } from 'mixpanel-browser';
import { useContext, useMemo } from 'react';
import { LogRocketContext } from './LogrocketProvider.tsx';
import { MixpanelContext } from './MixpanelProvider.tsx';

/**
 * Key-value data structure providing extra structured data to further describe an event beyond just the event name.
 */
export type AnalyticsData = {
  [key: string]:
    | null
    | undefined
    | string
    | string[]
    | number
    | number[]
    | boolean
    | boolean[];
};

/**
 * Interface allowing us to record product analytics events and data for later analysis.
 */
export interface MaterioAnalytics {
  /**
   *
   * @param event The name of the event that occurred (e.g., "Invoice paid", "Project created", "Contact updated")
   * @param data Extra structured data describing the event (e.g., invoice amount, project zip code, contact type, etc.)
   */
  track(event: string, data?: AnalyticsData): void;
}

/**
 * Hook to send event data to our internal analytics tools, allowing us to track user behavior in the UI.
 * The resulting object is meant to be used to track events after specific user interactions with the UI.
 * CAUTION: Do NOT call `analytics.track` inside the render function (this would send dupe events on every re-render)
 *
 * If analytics are enabled, the event data is sent to be stored in our 3rd party analytics tools.
 * If analytics are disabled, the event data is simply logged to the console.
 *
 * Example usage:
 *    const analytics = useAnalytics();
 *    const onButtonClicked = () => {
 *      // ... do logic to handle button click, then handle analytics:
 *      // ... notice how analytics are tracked only after a user interaction and NOT in the top-level render function.
 *      analytics.track('Project size updated', {
 *         projectId: 123456789,
 *         projectPhase: project.phase,
 *         oldSize: 'small',
 *         newSize: 'large',
 *       });
 *    }
 */
export function useAnalytics(): MaterioAnalytics {
  const mixpanel: Mixpanel | null =
    useContext(MixpanelContext) ?? window.mixpanel;

  const logrocket: typeof LogRocket | null =
    useContext(LogRocketContext) ?? window.LogRocket;

  return useMemo(() => {
    return {
      track(event: string, data: AnalyticsData) {
        // Send to mixpanel or log (primary analytics tool)
        if (mixpanel) {
          mixpanel.track(event, data);
        } else {
          console.log(`Analytics are currently disabled: ${event}`, data);
        }

        // Send to logrocket if able (secondary/experimental analytics tool)
        logrocket?.track(event, data);
      },
    };
  }, [mixpanel, logrocket]);
}
